import { useEffect, useState } from 'react';
import macName from '../lib/macName';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TypewriterText } from '../hooks/useTypewriter';
import CheckpointCard from '../components/CheckpointCard';
import RunRow from '../components/RunRow';
import NowRows, { nowItems } from '../components/NowRows';
import { LIVE_STATUSES } from '../components/RunCharts';

// Everything that's yours, at any visibility. The public square (search, profiles, the
// feed) shows published models only; this is the other side of that line.

const API = '/api';

function headers() {
  const h = {};
  const t = localStorage.getItem('logos_session_token');
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
}

const money = (cents) => `$${(cents / 100).toFixed(2)}`;
const FILTERS = ['all', 'private', 'unlisted', 'public'];

function Heading({ children, action }) {
  return (
    <div className="flex items-baseline gap-3 mb-1">
      <h2 className="font-mono text-[11px] text-[#666666] lowercase tracking-wide">{children}</h2>
      {action && <span className="ml-auto">{action}</span>}
    </div>
  );
}

function Action({ to, children, testid }) {
  return (
    <Link to={to} className="font-mono text-xs text-[#888888] hover:text-white lowercase" data-testid={testid}>{children}</Link>
  );
}

export default function Workspace() {
  const { user, loading, login } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!user) return undefined;
    let stop = false;
    const load = () => fetch(`${API}/me/workspace`, { headers: headers(), credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('could not load your workspace'))))
      .then((d) => { if (!stop) setData(d); })
      .catch((e) => { if (!stop) setError(e.message); });
    load();
    const timer = setInterval(load, 30000);
    return () => { stop = true; clearInterval(timer); };
  }, [user]);

  if (loading) return <div className="min-h-screen bg-black" />;
  if (!user) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4 px-6">
        <p className="text-[#888888] lowercase">your workspace is where your models and runs live.</p>
        <button onClick={login} className="border border-[#383f38] px-5 py-2 rounded-full text-white lowercase hover:bg-white hover:text-black transition-all">login</button>
      </div>
    );
  }

  const models = data?.models || [];
  const shown = filter === 'all' ? models : models.filter((m) => m.visibility === filter);
  const runs = data?.runs || [];
  const now = nowItems(data);
  const credit = data?.credit;
  const training = Boolean(credit?.training);

  const stats = data ? [
    ['models', models.length],
    ['private', data.counts.private],
    ['published', data.counts.public],
    training ? ['credit', credit.house ? 'house' : money(credit.balance_cents)] : ['linked apps', data.apps.length],
  ] : [];

  return (
    <div className="min-h-screen bg-black py-10 px-4 sm:px-6 lg:px-8 page-wrapper" data-testid="workspace-page">
      <div className="max-w-3xl mx-auto space-y-10">
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white lowercase font-mono"><TypewriterText text="workspace" speed={65} /></h1>
          <p className="text-[#aaaaaa] mt-3">
            yours, at any visibility. nobody else sees this page. models start private; publishing one puts it on the square
            and on your <Link to={`/u/${user.username}`} className="text-white hover:underline underline-offset-4 decoration-[#444444]">public profile</Link>.
          </p>
        </div>

        {error && <p className="font-mono text-sm text-[#ff8888] lowercase">{error}</p>}
        {!data && !error && <p className="font-mono text-sm text-[#666666] lowercase">loading…</p>}

        {data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 border-y border-[#1e251f] divide-[#1e251f] sm:divide-x" data-testid="workspace-stats">
              {stats.map(([label, value]) => (
                <div key={label} className="py-4 sm:px-4 first:sm:pl-0">
                  <div className="font-mono text-[10px] text-[#666666] lowercase tracking-wide">{label}</div>
                  <div className="font-mono text-xl sm:text-2xl text-white tabular-nums mt-1 truncate">{value}</div>
                </div>
              ))}
            </div>

            {now.length > 0 && (
              <section data-testid="workspace-now">
                <Heading action={<Action to="/train">train →</Action>}>now</Heading>
                <NowRows items={now} />
              </section>
            )}

            <section data-testid="workspace-models">
              <Heading action={<Action to="/upload" testid="workspace-upload">upload →</Action>}>models</Heading>
              {models.length > 0 && (
                <div className="flex gap-4 font-mono text-xs lowercase py-2">
                  {FILTERS.map((f) => {
                    const n = f === 'all' ? models.length : data.counts[f];
                    return (
                      <button key={f} onClick={() => setFilter(f)} disabled={f !== 'all' && !n}
                        className={`${filter === f ? 'text-white underline underline-offset-4 decoration-[#555555]' : 'text-[#777777] hover:text-white'} disabled:text-[#3a3f3a]`}
                        data-testid={`workspace-filter-${f}`}>
                        {f} {n}
                      </button>
                    );
                  })}
                </div>
              )}
              {models.length === 0 ? (
                <div className="border-y border-[#1a201b] py-6 space-y-2">
                  <p className="text-[#aaaaaa] lowercase text-sm">nothing here yet.</p>
                  <p className="text-[#777777] lowercase text-sm">
                    <Link to="/downloads" className="text-white hover:underline underline-offset-4">download soma</Link>, train it on any text,
                    and upload the model. it stays private until you publish it.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-[#1a201b] border-y border-[#1a201b]">
                  {shown.map((m) => <CheckpointCard key={m.checkpoint_id} checkpoint={m} own />)}
                </div>
              )}
            </section>

            <section data-testid="workspace-apps">
              <Heading action={<Action to="/link">manage →</Action>}>your macs</Heading>
              {data.apps.length === 0 ? (
                <p className="font-mono text-xs text-[#555555] lowercase py-3 border-y border-[#1a201b]">
                  none yet. sign in from the soma app to reach it from here: training, chat and backups, from any device.
                </p>
              ) : (
                <div className="divide-y divide-[#1a201b] border-y border-[#1a201b] font-mono text-sm">
                  {data.apps.map((a) => {
                    const t = a.state?.training || {};
                    const c = a.state?.chat || {};
                    const doing = t.running ? `training ${t.name || ''}${typeof t.recent_bpb === 'number' ? ` · ${t.recent_bpb.toFixed(3)} bpb` : ''}`
                      : c.loaded ? `chat · ${c.name || 'loaded'}` : a.online ? 'idle' : null;
                    return (
                      <Link key={a.token_id} to={`/remote/${a.token_id}`} className="py-2.5 flex items-center gap-3 group" data-testid="workspace-app">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: a.online ? 'rgb(77, 255, 133)' : '#444444' }} />
                        <span className="text-white lowercase truncate group-hover:underline underline-offset-4 decoration-[#444444]">{macName(a.name)}</span>
                        <span className="ml-auto text-xs text-[#888888] lowercase truncate">
                          {doing || (a.seen_at ? `seen ${new Date(a.seen_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).toLowerCase()}` : 'not seen yet')}
                        </span>
                        <span className="text-xs text-[#555555] group-hover:text-white shrink-0">open →</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </section>

            <section data-testid="workspace-data">
              <Heading action={<Action to="/data" testid="workspace-add-data">add data →</Action>}>data</Heading>
              {(data.sources || []).length === 0 ? (
                <p className="font-mono text-xs text-[#555555] lowercase py-3 border-y border-[#1a201b]">
                  nothing of your own yet. paste links or drop files on the <Link to="/data" className="text-white hover:underline underline-offset-4">data page</Link>.
                </p>
              ) : (
                <div className="divide-y divide-[#1a201b] border-y border-[#1a201b] font-mono text-sm">
                  {data.sources.map((src) => (
                    <Link key={src.source_id} to="/data" className="py-2.5 flex items-baseline gap-3 group">
                      <span className="text-white lowercase truncate group-hover:underline underline-offset-4 decoration-[#444444]">{src.label}</span>
                      <span className="font-mono text-[10px] text-[#888888] lowercase">{src.streamed ? 'streamed' : src.status}</span>
                      <span className="ml-auto text-xs text-[#666666] shrink-0">{src.bytes ? `${(src.bytes / 1e6).toFixed(src.bytes >= 1e8 ? 0 : 1)} mb` : ''}</span>
                    </Link>
                  ))}
                </div>
              )}
            </section>

            {(training || runs.length > 0) && (
              <section data-testid="workspace-runs">
                <Heading action={training && <Action to="/train">train →</Action>}>runs</Heading>
                {runs.length === 0 ? (
                  <p className="font-mono text-xs text-[#555555] lowercase py-3 border-y border-[#1a201b]">no cloud runs yet</p>
                ) : (
                  <div className="divide-y divide-[#1a201b] border-y border-[#1a201b]">
                    {runs.filter((r) => !LIVE_STATUSES.includes(r.status)).slice(0, 8).map((r) => <RunRow key={r.run_id} run={r} />)}
                  </div>
                )}
              </section>
            )}

            {training && (
              <section data-testid="workspace-credit">
                <Heading action={<Action to="/credits">ledger →</Action>}>credit</Heading>
                <div className="border-y border-[#1a201b] py-3 flex items-baseline gap-4">
                  <span className="font-mono text-white tabular-nums">{credit.house ? 'house' : `${money(credit.balance_cents)} usd`}</span>
                  <span className="font-mono text-xs text-[#666666] lowercase">
                    {credit.house ? 'your runs are at cost and never touch credit.' : 'cloud runs are paid from credit.'}
                  </span>
                </div>
              </section>
            )}

            <CloseAccount username={user.username} />
          </>
        )}
      </div>
    </div>
  );
}

function CloseAccount({ username }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [published, setPublished] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const close = async () => {
    setBusy(true); setError(null);
    const res = await fetch(`${API}/me/close`, { method: 'POST', credentials: 'include',
      headers: { ...headers(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ confirm: typed, remove_published: published }) });
    const body = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) { setError(body.detail || 'the account was not closed'); return; }
    localStorage.removeItem('logos_session_token');
    window.location.href = '/';
  };
  return (
    <section className="pt-6" data-testid="workspace-close">
      {!open ? (
        <button onClick={() => setOpen(true)} className="font-mono text-[11px] text-[#555555] hover:text-[#ff8888] lowercase">close my account</button>
      ) : (
        <div className="border border-[#3a2222] rounded-lg p-4 space-y-3">
          <p className="text-[#cccccc] text-sm">
            closing your account deletes your private and unlisted models, your data and uploads, your linked apps,
            comments and follows, for good. published models stay up unless you tick the box. your credit
            history is kept as an anonymous record. you can't close it while a cloud run is going or credit is left.
          </p>
          <label className="flex items-center gap-2 font-mono text-xs text-[#aaaaaa] lowercase">
            <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
            also delete my published models
          </label>
          <div className="flex gap-2 flex-wrap items-center">
            <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={`type ${username} to confirm`}
              className="bg-transparent border border-[#383f38] rounded-lg px-3 py-1.5 text-white font-mono text-xs outline-none focus:border-white placeholder:text-[#555555]" />
            <button onClick={close} disabled={busy || typed !== username}
              className="border border-[#ff5c5c] text-[#ff8888] px-4 py-1.5 rounded-full font-mono text-xs lowercase hover:bg-[#ff5c5c] hover:text-black transition-all disabled:opacity-40">
              {busy ? 'closing…' : 'close account'}
            </button>
            <button onClick={() => { setOpen(false); setTyped(''); }} className="font-mono text-xs text-[#888888] hover:text-white lowercase">keep it</button>
          </div>
          {error && <p className="font-mono text-xs text-[#ff8888] lowercase">{error}</p>}
        </div>
      )}
    </section>
  );
}
